import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { KPIService } from '../../api/kpi/KPIService';
import KpiChart from '../../components/Chart/KpiChart';
import {
  FaCogs, FaTachometerAlt,
  FaChevronLeft, FaChevronRight, FaChartLine, FaTruck, FaBug
} from 'react-icons/fa';
import '../Dashboard/Dashboard.css';

const KPI_ICON_MAP = {
  'P': FaChartLine,  // 생산성
  'C': FaCogs,       // 원가
  'D': FaTruck,      // 납기
  'Q': FaBug,        // 품질
};

const KPI_COLOR_MAP = {
  'P': '#8884d8',
  'C': '#ffc658',
  'D': '#82ca9d',
  'Q': '#ff4d4f',
};

const DashboardIqCluster = () => {
  const session = useSelector((state) => state.session.data);

  const [kpiLoading, setKpiLoading] = useState(true);

  // 업체별 외부 KPI 지표
  const [indicators, setIndicators] = useState([]);
  const [externalKpiData, setExternalKpiData] = useState({});

  // 월 선택 상태
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
  }, [selectedYear, selectedMonth]);

  const dateRange = useMemo(() => {
    const y = selectedYear;
    const m = String(selectedMonth).padStart(2, '0');
    const now = new Date();
    let endDay;
    if (isCurrentMonth) {
      endDay = String(now.getDate()).padStart(2, '0');
    } else {
      endDay = String(new Date(y, selectedMonth, 0).getDate()).padStart(2, '0');
    }
    return { start: `${y}-${m}-01`, end: `${y}-${m}-${endDay}` };
  }, [selectedYear, selectedMonth, isCurrentMonth]);

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    const now = new Date();
    const nextM = selectedMonth === 12 ? 1 : selectedMonth + 1;
    const nextY = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
    if (nextY > now.getFullYear() || (nextY === now.getFullYear() && nextM > now.getMonth() + 1)) return;
    setSelectedYear(nextY);
    setSelectedMonth(nextM);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
  };

  // 업체 KPI 지표 로드
  useEffect(() => {
    if (!session?.companySeq) return;
    KPIService.indicators({ companySeq: session.companySeq })
      .then(data => { if (data) setIndicators(data); })
      .catch(() => {});
  }, [session?.companySeq]);

  // 외부 KPI 데이터 로드 (지표 기준)
  useEffect(() => {
    if (!session?.companySeq || indicators.length === 0) {
      setKpiLoading(false);
      return;
    }
    const params = {
      companySeq: session.companySeq,
      companyName: session.companyName,
      start: dateRange.start,
      end: dateRange.end,
    };

    setKpiLoading(true);

    const promises = indicators.map(ind =>
      KPIService.TimeSeries.externalData({
        ...params,
        fldCd: ind.kpiFldCd,
        dtlCd: ind.kpiDtlCd,
      }).then(data => ({ key: `${ind.kpiFldCd}_${ind.kpiDtlCd}`, data: data || [] }))
        .catch(() => ({ key: `${ind.kpiFldCd}_${ind.kpiDtlCd}`, data: [] }))
    );

    Promise.all(promises).then((results) => {
      const extMap = {};
      results.forEach(r => { extMap[r.key] = r.data; });
      setExternalKpiData(extMap);
      setKpiLoading(false);
    });
  }, [session?.companySeq, dateRange.start, dateRange.end, indicators]);

  // 외부 KPI별 평균값 계산
  const getExternalAvg = (key) => {
    const data = externalKpiData[key] || [];
    if (data.length === 0) return null;
    const nums = data.map(d => parseFloat(d.numeric || d.value || '0')).filter(n => !isNaN(n));
    if (nums.length === 0) return null;
    return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
  };

  // 외부 KPI별 차트 데이터
  const getExternalChartData = (key) => {
    const data = externalKpiData[key] || [];
    return data.map(d => ({
      name: String(d.name).length > 5 ? String(d.name).slice(5) : String(d.name),
      value: parseFloat(d.numeric || d.value || '0'),
    }));
  };

  const monthLabel = `${selectedYear}년 ${selectedMonth}월`;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h3><FaTachometerAlt style={{ marginRight: 8 }} />대시보드</h3>
        </div>
        <div className="month-picker">
          <button className="month-picker-btn" onClick={goToPrevMonth}><FaChevronLeft /></button>
          <span className="month-picker-label" onClick={goToCurrentMonth} title="현재 월로 이동">
            {monthLabel}
          </span>
          <button className="month-picker-btn" onClick={goToNextMonth} disabled={isCurrentMonth}>
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* KPI 요약 카드 — 측정 지표만 표시 */}
      <div className="dashboard-section">
        <h4 className="section-title">이번 달 KPI 요약</h4>
        {indicators.length > 0 ? (
          <div className="kpi-summary-row">
            {indicators.map(ind => {
              const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
              const avg = getExternalAvg(key);
              const Icon = KPI_ICON_MAP[ind.kpiFldCd] || FaChartLine;
              const color = KPI_COLOR_MAP[ind.kpiFldCd] || '#aaa';
              return (
                <div className="kpi-stat-card" key={key}>
                  <div className="kpi-stat-icon" style={{ color }}>
                    <Icon size={24} />
                  </div>
                  <div className="kpi-stat-body">
                    <span className="kpi-stat-label">{ind.indicatorName}</span>
                    <span className="kpi-stat-value">
                      {kpiLoading ? '...' : avg ? `${avg}${ind.unit || ''}` : '-'}
                    </span>
                    {ind.targetValue != null && (
                      <span className="kpi-stat-target">
                        목표: {ind.targetValue}{ind.unit || ''}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data-placeholder">
            {kpiLoading ? '로딩 중...' : '등록된 KPI 지표가 없습니다.'}
          </div>
        )}
      </div>

      {/* 월간 통계 — 측정 지표만 표시 */}
      {indicators.length > 0 && (
        <div className="dashboard-section">
          <h4 className="section-title">월간 통계</h4>
          <div className="chart-row">
            {indicators.map(ind => {
              const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
              const chartData = getExternalChartData(key);
              return (
                <div className="chart-panel" key={key}>
                  <h5 className="chart-panel-title">일별 {ind.indicatorName} 추이</h5>
                  <div className="chart-container">
                    {kpiLoading ? (
                      <div className="no-data-placeholder">로딩 중...</div>
                    ) : chartData.length > 0 ? (
                      <KpiChart label={`${ind.indicatorName} (${ind.unit || ''})`} value={chartData} />
                    ) : (
                      <div className="no-data-placeholder">데이터 없음</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardIqCluster;
